import {
  Component,
  OnInit,
  signal,
  linkedSignal,
  input,
  output,
  computed,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideSearch,
  lucideBatteryCharging,
  lucideCheck,
  lucideChevronDown,
  lucidePlus,
  lucideX,
} from '@ng-icons/lucide';
import { BrnPopoverImports } from '@spartan-ng/brain/popover';
import { BrnPopoverComponent } from '@spartan-ng/brain/popover';
import { BrnCommandImports } from '@spartan-ng/brain/command';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { HlmCommandImports } from '@spartan-ng/helm/command';
import { SolarStorage } from '@features/catalog/models/storage.model';

@Component({
  selector: 'app-storage-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgIconComponent,
    ...BrnPopoverImports,
    ...BrnCommandImports,
    ...HlmPopoverImports,
    ...HlmCommandImports,
  ],
  providers: [
    provideIcons({
      lucideSearch,
      lucideBatteryCharging,
      lucideCheck,
      lucideChevronDown,
      lucidePlus,
      lucideX,
    }),
  ],
  templateUrl: './storage-selector.html',
  styleUrl: './storage-selector.css',
})
export class StorageSelector {
  // ViewChild für Popover-Referenz
  @ViewChild(BrnPopoverComponent, { static: true })
  popover!: BrnPopoverComponent;

  // Inputs — data is owned and fetched by the parent smart component
  storages = input<SolarStorage[]>([]);
  selectedStorageIds = input<string[]>([]);
  placeholder = input<string>('Speicher auswählen (0-2, optional)...');
  disabled = input<boolean>(false);
  label = input<string>('Speicher');
  minStorages = input<number>(0);
  maxStorages = input<number>(2);
  isLoading = input<boolean>(false);
  error = input<string | null>(null);

  // Outputs
  storagesSelected = output<SolarStorage[]>();
  selectedStorageIdsChange = output<string[]>();

  // Local selection state — independently mutable (user toggles items)
  selectedStoragesLocal = linkedSignal(() => {
    const selectedIds = new Set(this.selectedStorageIds());
    return this.storages().filter((storage) => selectedIds.has(storage.id));
  });
  searchValue = signal('');

  // Computed
  filteredStorages = computed(() => {
    const search = this.searchValue().toLowerCase();
    return this.storages().filter((storage) =>
      `${storage.name} ${storage.size} ${storage.power} ${storage.cost}`
        .toLowerCase()
        .includes(search),
    );
  });

  toggleStorage(storage: SolarStorage): void {
    const selected = this.selectedStoragesLocal();
    const isSelected = selected.some((s) => s.id === storage.id);

    if (isSelected) {
      this.removeStorage(storage.id);
    } else {
      if (selected.length < this.maxStorages()) {
        const newSelected = [...selected, storage];
        this.selectedStoragesLocal.set(newSelected);
        this.emitSelectionChange(newSelected);
      }
    }
  }

  removeStorage(storageId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const newSelected = this.selectedStoragesLocal().filter(
      (s) => s.id !== storageId,
    );
    this.selectedStoragesLocal.set(newSelected);
    this.emitSelectionChange(newSelected);
  }

  isStorageSelected(storageId: string): boolean {
    return this.selectedStoragesLocal().some((s) => s.id === storageId);
  }

  private emitSelectionChange(storages: SolarStorage[]): void {
    const ids = storages.map((s) => s.id);
    this.storagesSelected.emit(storages);
    this.selectedStorageIdsChange.emit(ids);
  }

  getStorageSearchValue(storage: SolarStorage): string {
    return `${storage.name} ${storage.size}kWh ${storage.power || ''}kW ${
      storage.cost
    }€`;
  }

  getStorageDisplay(storage: SolarStorage): string {
    return `${storage.name} (${storage.size}kWh${
      storage.power ? ' • ' + storage.power + 'kW' : ''
    } • ${storage.cost}€)`;
  }

  getStorageById(storageId: string): SolarStorage | undefined {
    return this.storages().find((s) => s.id === storageId);
  }

  isButtonDisabled(): boolean {
    return (
      this.disabled() ||
      this.selectedStoragesLocal().length >= this.maxStorages()
    );
  }

  getButtonLabel(): string {
    const selected = this.selectedStoragesLocal().length;
    return `${selected} von ${this.maxStorages()} Speichern ausgewählt`;
  }
}
